import { AloftPrice } from '@aloft/toolkit-shared';
import { Block, Flex, SimpleInput, Typography } from '@aloft/toolkit-sui';

import { ContentBlock } from '../../../components/Analysis/ColumnComponents/ContentBlock.tsx';
import { DetailLineWithValue } from '../../../components/Analysis/ColumnComponents/DetailLineWithValue.tsx';
import { setDepreciation } from '../../../redux/features/actions/setDepreciation.ts';
import { toggleDepreciationSettings } from '../../../redux/features/orderSlice.ts';
import {
  selectExternalDepreciationDisplay,
  selectFunctionalDepreciationDisplay,
  selectPhysicalDepreciation,
  selectPhysicalDepreciationDisplay,
} from '../../../redux/features/selectors/depreciationSelectors.ts';
import { selectDepreciationSettings } from '../../../redux/features/selectors/orderSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { RootState } from '../../../redux/types.ts';
import { PLACEHOLDER } from '../../analysis/consts.ts';

const EditableLine = ({
  inputId,
  label,
  value,
  onChange,
}: {
  inputId: string;
  label: string;
  value: string;
  onChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}) => (
  <Flex justifyContent="space-between" gap=".25rem" flexWrap="nowrap" alignItems="center">
    <Flex minWidth="0" maxWidth="70%" flexBasis="60%">
      <Typography color="textTertiary">{label}</Typography>
    </Flex>
    <Flex justifyContent="flex-end" flex={1}>
      <SimpleInput
        id={inputId}
        type="number"
        value={value}
        onChange={onChange}
        precision={0}
        size="sm"
        leftAddon={
          <Typography variant="body1" color="textDisabled">
            $
          </Typography>
        }
        useSeparator
        width="7rem"
        min={0}
        textAlign="right"
      />
    </Flex>
  </Flex>
);

export const DepreciationBreakdown = ({ id }: { id: string }) => {
  const dispatch = useAppDispatch();

  const { enableFunctionalDepreciation, enableExternalDepreciation } = useAppSelector(
    selectDepreciationSettings,
  );

  const { physical, physicalPrice, functionalDisplay, externalDisplay } = useAppSelector(
    (state: RootState) => ({
      physical: selectPhysicalDepreciationDisplay(state, id),
      physicalPrice: selectPhysicalDepreciation(state, id),
      functionalDisplay: selectFunctionalDepreciationDisplay(state, id),
      externalDisplay: selectExternalDepreciationDisplay(state, id),
    }),
  );

  const totalDisplay = physicalPrice
    ?.mul(-1)
    .sub(AloftPrice.fromDollarLike(functionalDisplay ?? '0') ?? AloftPrice.zero)
    .sub(AloftPrice.fromDollarLike(externalDisplay ?? '0') ?? AloftPrice.zero)
    .roundToDollarLike()
    .toString(true);

  const functionalInputValue = (functionalDisplay ?? '0').replace(/[^0-9.-]/g, '') || '0';
  const externalInputValue = (externalDisplay ?? '0').replace(/[^0-9.-]/g, '') || '0';

  const handleFunctionalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? 0 : Number(event.target.value);
    if (newValue > 0 && !enableFunctionalDepreciation) {
      dispatch(toggleDepreciationSettings('functional'));
    }
    dispatch(
      setDepreciation({
        id,
        field: 'functionalDepreciation',
        newValue,
      }),
    );
  };

  const handleExternalChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const newValue = event.target.value === '' ? 0 : Number(event.target.value);
    if (newValue > 0 && !enableExternalDepreciation) {
      dispatch(toggleDepreciationSettings('external'));
    }
    dispatch(
      setDepreciation({
        id,
        field: 'externalDepreciation',
        newValue,
      }),
    );
  };

  return (
    <Block
      backgroundColor="surface1"
      width="100%"
      __dangerDoNotUseSx={{
        width: 'calc(100% + 2rem)',
        marginLeft: '-1rem',
        marginRight: '-1rem',
        paddingLeft: '1rem',
        paddingRight: '1rem',
        boxSizing: 'border-box',
      }}
    >
      <ContentBlock>
        <Flex justifyContent="space-between" alignItems="baseline" gap="1rem">
          <Typography variant="h4" color="textPrimary">
            Total Depreciation
          </Typography>
          <Typography variant="h4" color="textPrimary">
            {totalDisplay ?? PLACEHOLDER}
          </Typography>
        </Flex>
        <Flex flexDirection="column" marginTop="0.5rem" gap="0.25rem">
          <DetailLineWithValue
            text="Physical"
            value={physical ?? ''}
            textColor="textTertiary"
            valueColor="textSecondary"
          />
          <EditableLine
            inputId={`${id}-Functional`}
            label="Functional"
            value={functionalInputValue}
            onChange={handleFunctionalChange}
          />
          <EditableLine
            inputId={`${id}-External`}
            label="External"
            value={externalInputValue}
            onChange={handleExternalChange}
          />
        </Flex>
      </ContentBlock>
    </Block>
  );
};
